import React, { useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';
import { ErrorBanner } from './Configuration';
import { resolveLinkIcon } from './ProjectLinks';

// Real data as of 2026-09-17 -- replaces the earlier hardcoded "coming soon" stub. `/project-links`
// already excludes `internalOnly` rows for an external caller (ProjectLinksService.findAll), so
// there's no client-side filtering to keep in sync with the server, same reasoning as
// CustomerProjectSchedule's /projects call.
export default function CustomerProjectLinks() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await apiFetch('/projects');
        if (cancelled) return;
        setProjects(rows);
        // A single-project customer opens straight onto it -- no picker to click through. `loading`
        // stays true here rather than clearing it: this render would otherwise briefly show "No
        // links yet" (projectId set, loading false, links still []) before the second effect's own
        // fetch starts and sets loading back to true a render later.
        if (rows.length === 1) setProjectId(rows[0].id);
        else setLoading(false);
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!projectId) { setLinks([]); return; }
    let cancelled = false;
    setLoading(true);
    apiFetch(`/project-links?projectId=${projectId}`)
      .then((rows) => { if (!cancelled) setLinks(rows); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services · Project Links</div>
        <h1 className="page-title">Project Links</h1>
        <p className="page-subtitle">Quick links to the other places your project lives.</p>
      </div>

      {projects.length > 1 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="form-group" style={{ marginBottom: 0, maxWidth: 420 }}>
            <label className="form-label" htmlFor="customer-links-project">Project</label>
            <select
              id="customer-links-project"
              className="form-input"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">— Select a project —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <ErrorBanner error={error} />

      {!loading && !projects.length && !error && (
        <div className="placeholder-page">
          <div className="placeholder-icon">🗂️</div>
          <div className="placeholder-title">No projects available yet</div>
          <div className="placeholder-sub">Reach out to your Aumentum contact if this doesn't look right.</div>
        </div>
      )}

      {projectId && (
        loading ? (
          <div className="card" style={{ color: 'var(--text-mid)', fontSize: 13 }}>Loading…</div>
        ) : !links.length ? (
          <div className="placeholder-page">
            <div className="placeholder-icon">🔗</div>
            <div className="placeholder-title">No links yet</div>
            <div className="placeholder-sub">Your Aumentum project team hasn't added any links here yet.</div>
          </div>
        ) : (
          // Same tile grid as the internal page's default view (ProjectLinks.js), including its icon picker.
          <div className="link-tile-grid">
            {links.map((link) => {
              const Icon = resolveLinkIcon(link);
              return (
                <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="link-tile" title={link.notes || link.label}>
                  <div className="link-tile-icon"><Icon size={26} strokeWidth={1.6} aria-hidden /></div>
                  <div className="link-tile-label">{link.label}</div>
                  {link.notes && <div className="link-tile-notes">{link.notes}</div>}
                </a>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
